/* istanbul ignore file - need to fix flakey tests */
import { faArrowsTurnRight, faFloppyDisk, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGoogleAnalytics } from '@partful/shared/lib/hooks/useGoogleAnalytics';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { PopoverMenu, PopoverMenuItem } from '@partful/shared/ui/PopoverMenu';
import { useConfiguration } from '@providers/Configuration';
import { CategoryNode } from '@services/productInventory';
import { MRT_Row } from 'material-react-table';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartsCategories } from '../../lib/context/PartsCategoriesContext';
import { useSavePartsCategories } from '../../lib/hooks/useSavePartsCategories';
import { getSelectedRows } from '../../lib/utils';
import style from './SavePartsCategories.module.scss';

export function SavePartsCategories({
  selectedRows,
  onSelectRowsToMove,
  resetRowSelection,
}: {
  selectedRows: MRT_Row<CategoryNode>[];
  onSelectRowsToMove: (rows: CategoryNode[]) => void;
  resetRowSelection: () => void;
}) {
  const { t: i18n } = useTranslation('manageProduct');
  const { t: i18nCommon } = useTranslation('common');
  const { sendEvent } = useGoogleAnalytics();
  const { state, reset, deleteCategory } = usePartsCategories();
  const { savePartCategories, isProcessing } = useSavePartsCategories({ newTableData: state });
  const { confirm, close } = useConfirmation();
  const { name } = useConfiguration();

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const onDiscardChanges = () => {
    confirm({
      title: i18n('partsCategories.interactive.confirmDiscard.title', { name }),
      message: i18n('partsCategories.interactive.confirmDiscard.message'),
      confirmText: i18n('partsCategories.interactive.confirmDiscard.confirm'),
      cancelText: i18n('partsCategories.interactive.confirmDiscard.cancel'),
      onConfirm: async () => {
        reset(state);
        close();
      },
    });
  };

  const filteredRows = getSelectedRows(selectedRows);
  const showBulkActions = filteredRows.length > 1;
  const showDeleteAction = filteredRows.every((row) => row.original.isCategory && row.original.children.length === 0);

  return (
    <div className={style.wrapper} data-testid="floating-actions">
      {showBulkActions ? (
        <div className={style.bulkActions}>
          <span>
            <strong>{filteredRows.length}</strong> {i18n('partsCategories.interactive.rowsSelected')}
          </span>
          <Button
            size="small"
            rounded
            ref={buttonRef}
            onClick={() => {
              setIsPopoverOpen(true);
            }}
            aria-haspopup="true"
            aria-expanded={isPopoverOpen ? 'true' : undefined}
          >
            {i18n('partsCategories.interactive.actions')}
          </Button>
          <PopoverMenu
            anchorEl={buttonRef.current}
            open={isPopoverOpen}
            onClose={/* istanbul ignore next - don't need to assert it closes */ () => setIsPopoverOpen(false)}
          >
            <PopoverMenuItem
              key="move-multiple"
              aria-label={`Move selected (${filteredRows.length}) `}
              data-testid="move-multiple"
              onClick={() => {
                onSelectRowsToMove(filteredRows.map((selectedRow) => selectedRow.original));
                setIsPopoverOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faArrowsTurnRight} />
              {i18n('partsCategories.interactive.moveMultipleTo', { count: filteredRows.length })}
            </PopoverMenuItem>
            {showDeleteAction ? (
              <PopoverMenuItem
                key="delete-multiple"
                aria-label={`Delete selected (${filteredRows.length}) `}
                data-testid="delete-multiple"
                onClick={() => {
                  confirm({
                    title: i18n('partsCategories.interactive.deleteConfirmationMultiple', {
                      count: filteredRows.length,
                    }),
                    confirmText: i18nCommon('yesDelete'),
                    cancelText: i18nCommon('cancel'),
                    message: '',
                    onConfirm: async () => {
                      resetRowSelection();
                      await Promise.all(filteredRows.map((row) => deleteCategory(row.original)));
                      close();
                      sendEvent('pcui_delete_multiple');
                    },
                  });
                  setIsPopoverOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                {i18n('partsCategories.interactive.deleteMultiple', { count: filteredRows.length })}
              </PopoverMenuItem>
            ) : null}
          </PopoverMenu>
        </div>
      ) : null}
      <div className={style.coreActions}>
        <Button rounded onClick={savePartCategories} disabled={isProcessing}>
          <FontAwesomeIcon icon={faFloppyDisk} size="xl" />
          {i18n('partsCategories.interactive.processChanges')}
        </Button>
        <Button variant="tertiary" size="small" rounded onClick={onDiscardChanges} disabled={isProcessing}>
          {i18n('partsCategories.interactive.discardChanges')}
        </Button>
      </div>
    </div>
  );
}
